import { GzHeaderView } from '../view/GzHeaderView';
import { templateGzHeader } from '../view/template';
import { BaseWebComponentController, IListenerConfig } from '../../../framework/BaseWebComponentController';
import { GzHeaderWebComponent } from '..';
import { translateFn } from '../../../utils/translator.helper';
import { html } from 'lit';
import { NavigationConstants, NavigationHelper } from '@nirvana/shared/navigation';
import { FeaturesConstants, HeaderItemEvents, HeaderMenuItem } from '../types';
import { UserRoleWebComponent } from '../ui/userRole';
import { isVisible } from '../../../utils/htmlElement.utils';
import { SelectOverlayElement } from '../../gzSelect';
import { ConsoleRequest, DataProviderEntity, DataProviderType } from '../../../utils/request';
import { ThemeController } from '@nirvana/shared/color-theme';
// tslint:disable-next-line:nx-enforce-module-boundaries
import { Theme } from 'libs/shared/color-theme/src/lib/types';

export class GzHeaderController extends BaseWebComponentController {
  public component: GzHeaderWebComponent;
  public userRoleComponent: UserRoleWebComponent;

  public view: GzHeaderView;
  public viewConstructor: typeof GzHeaderView;
  public themeController: ThemeController;

  private headerMenuItems: HeaderMenuItem[] = [
    {
      id: NavigationConstants.MY_ACCOUNT.NAME,
      icon: 'ds-icon-user_menu',
      name: translateFn('User menu'),
      isActive: false,
      isVisible: true,
      content: html`<user-role
        .navigateToMyAccount="${this.navigateToMyAccount.bind(this)}"
        .navigateToMyCompany="${this.navigateToMyCompany.bind(this)}"
        .navigateToMyIntegrations="${this.navigateToMyIntegrations.bind(this)}"
        .navigateToCredentialsManager="${this.navigateToCredentialsManager.bind(this)}"
        .showWhatsNew="${this.showWhatsNew.bind(this)}"
        .logoutUser="${this.logoutUser.bind(this)}"
        .showSupportPanel="${this.showSupportPanel.bind(this)}"
        .showFeedbackForm="${this.showFeedbackForm.bind(this)}"
        .handleDarkTheme="${this.handleDarkTheme.bind(this)}"
      >
      </user-role>`,
    },
    {
      id: NavigationConstants.IN_PRODUCT_TRIALS.NAME,
      icon: 'ds-icon-product_trials_hub',
      name: translateFn('Products Hub'),
      isActive: false,
      isVisible: false,
      handler: () => this.navigateToInProductTrialsHub(),
    },
    {
      id: NavigationConstants.WHATS_NEW.NAME,
      icon: 'ds-icon-whats_new',
      name: translateFn(`What's new`),
      isActive: false,
      isVisible: true,
      disableAutoClearActive: true,
      handler: () => this.showWhatsNew(),
    },
    {
      id: NavigationConstants.HELP_MODE.NAME,
      icon: 'ds-icon-help_support',
      name: translateFn('Help'),
      isActive: false,
      isVisible: false,
      handler: () => this.toggleHelpMode(),
    },
    {
      id: NavigationConstants.NOTIFICATIONS.NAME,
      icon: 'ds-icon-notification_bell',
      name: translateFn('Notifications'),
      isActive: false,
      isVisible: true,
      disableAutoClearActive: true,
      handler: () => this.showNotifications(),
    },
  ];

  constructor(component: GzHeaderWebComponent) {
    super(component);
    this.userRoleComponent = new UserRoleWebComponent();
    this.initialSetup({
      viewConstructor: GzHeaderView,
      viewTemplate: templateGzHeader,
    });
    this.themeController = new ThemeController(this.userRoleComponent);
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$headerUi,
        event: HeaderItemEvents.MENU_OPEN,
        callback: () => this.onMenuItemOverlayOpen(),
      },
    ];
  }

  private closeOverlay(): void {
    const overlay = window.document.querySelector('select-overlay') as SelectOverlayElement;
    this.view.dom.$headerUi.clearOverlayButtonId();
    if (!overlay) {
      return;
    }
    overlay.close();
    this.setMenuItemActive(NavigationConstants.MY_ACCOUNT.NAME, false);
    this.getMenuItem(NavigationConstants.MY_ACCOUNT.NAME)?.classList.remove('active');
  }

  private navigateToMyAccount(): void {
    this.closeOverlay();
    NavigationHelper.navigateTo('/my-account');
  }

  private async navigateToMyCompany(): Promise<void> {
    this.closeOverlay();
    NavigationHelper.navigateTo('/my-company');
  }

  private navigateToCredentialsManager(): void {
    this.closeOverlay();
    NavigationHelper.navigateTo('/credentials-manager');
  }

  private showWhatsNew(): void {
    const controller = this.dependencies.Ext.getController(NavigationConstants.WHATS_NEW.CONTROLLER);
    this.closeOverlay();
    if (controller.whatsNewPanelController.isVisible()) {
      controller.hideWhatsNewPanel();
      this.setMenuItemActive(NavigationConstants.WHATS_NEW.NAME, false);
    } else {
      this.setMenuItemActive(NavigationConstants.NOTIFICATIONS.NAME, false);
      controller.showWhatsNewPanel();
      this.setMenuItemActive(NavigationConstants.WHATS_NEW.NAME, true);
    }
  }

  private showNotifications(): void {
    const controller = this.dependencies.Ext.getController(NavigationConstants.NOTIFICATIONS.CONTROLLER);
    const notificationPanel = document.querySelector('.notificationPanel');
    this.closeOverlay();
    if (isVisible(notificationPanel as HTMLElement)) {
      //here we use same method to hide notifications
      controller.showSideNotifications();
      this.setMenuItemActive(NavigationConstants.NOTIFICATIONS.NAME, false);
    } else {
      this.setMenuItemActive(NavigationConstants.WHATS_NEW.NAME, false);
      controller.showSideNotifications();
      this.setMenuItemActive(NavigationConstants.NOTIFICATIONS.NAME, true);
    }
  }

  private showFeedbackForm(): void {
    this.closeOverlay();
    NavigationHelper.navigateTo('/feedback');
  }

  private navigateToInProductTrialsHub(): void {
    this.closeOverlay();
    NavigationHelper.navigateToProductsHub();
  }

  private navigateToMyIntegrations(): void {
    this.closeOverlay();
    NavigationHelper.navigateTo('/integrations');
  }

  private logoutUser(): void {
    this.closeOverlay();
    this.dependencies.Ext.logoutUser();
  }

  public handleDarkTheme(mode: Theme): void {
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    this.userRoleComponent = window.document.querySelector('user-role') as UserRoleWebComponent;

    if (mode === Theme.AUTO) {
      this.themeController.setTheme(systemPrefersDark ? Theme.DARK : Theme.LIGHT);
    } else {
      this.themeController.setTheme(mode);
    }

    this.updateThemeButtonState(mode);
    // uncomment this piece of code when backend is ready

    // ConsoleRequest.createRequest<any>({
    //   dataProviderType: DataProviderType.CORE,
    //   dataProviderEntity: DataProviderEntity.MODEL,
    //   endpoint: 'AdditionalUserSettings',
    //   method: 'setDarkMode',
    //   payload: { darkMode: mode },
    // });
    localStorage.setItem('theme', mode);
  }

  private updateThemeButtonState(theme: Theme): void {
    this.userRoleComponent.themeButtons.forEach((button: HTMLElement) => {
      button.classList.contains(theme) ? button.classList.add('active') : button.classList.remove('active');
    });
  }

  private toggleHelpMode(): void {
    const { helpMode } = this.dependencies.Ext;
    helpMode.toggle();
    const isHelpModeActive = window.document.querySelectorAll('.helpModeContainer').length !== 0;
    if (isHelpModeActive) {
      this.setMenuItemActive(NavigationConstants.HELP_MODE.NAME, true);
    } else {
      this.setMenuItemActive(NavigationConstants.HELP_MODE.NAME, false);
    }
  }

  private showSupportPanel(): void {
    this.closeOverlay();
    NavigationHelper.navigateTo('/help-and-support');
  }

  public clearActive(): void {
    this.headerMenuItems = this.headerMenuItems.map((item: HeaderMenuItem) => {
      return {
        ...item,
        isActive: item.disableAutoClearActive ? item.isActive : false,
      };
    });
    this.render();
  }

  public setMenuItemVisibility(id: string, _isVisible: boolean): void {
    this.headerMenuItems = this.headerMenuItems.map((item: HeaderMenuItem) => {
      if (item.id !== id) {
        return item;
      }

      return {
        ...item,
        isVisible: _isVisible,
      };
    });
    this.render();
  }

  public setMenuItemActive(id: string, _isActive: boolean): void {
    this.headerMenuItems = this.headerMenuItems.map((item: HeaderMenuItem) => {
      if (item.id !== id) {
        return item;
      }

      return {
        ...item,
        isActive: _isActive,
      };
    });
    this.render();
  }

  public updatNotificationsBadgeContent(content: number): void {
    this.headerMenuItems = this.headerMenuItems.map((item: HeaderMenuItem) => {
      if (item.id !== NavigationConstants.NOTIFICATIONS.NAME) {
        return item;
      }

      return {
        ...item,
        badgeContent: content,
      };
    });
    this.render();
  }

  public async getSavedTheme(): Promise<Theme> {
    const response: { darkModeState: Theme } = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.CORE,
      dataProviderEntity: DataProviderEntity.MODEL,
      endpoint: 'AdditionalUserSettings',
      method: 'getDarkThemeSettings',
    });
    return response.darkModeState;
  }

  public getMenuItem(id: string): HTMLElement | undefined {
    return this.view.dom.$headerUi.getMenuItem(id);
  }

  async getDataForView(): Promise<HeaderMenuItem[]> {
    return this.headerMenuItems;
  }

  private setAccountData(): void {
    const userAccount = window.document.querySelector('user-role') as UserRoleWebComponent;
    const userName = this.dependencies.getUser().profile.fullName;

    if (!userAccount) {
      return;
    }

    if (!userAccount.userName || userAccount.userName !== userName) {
      userAccount.userName = userName;
    }
  }

  public setUserFeaturesAndRights(isCloud?: boolean): void {
    const userAccount = window.document.querySelector('user-role') as UserRoleWebComponent;
    if (!userAccount) {
      return;
    }

    const features = [
      FeaturesConstants.COMPANION_STANDALONE,
      FeaturesConstants.SVE_SERVICE,
      FeaturesConstants.EPS_SERVICE,
    ];
    const [companion, sve_service, eps_service] = features.map(feature => this.dependencies.Ext.hasFeatures([feature]));
    const { manageCompanies, manageInventory, companyManager, manageReports } = this.dependencies.getUser().role.rights;

    userAccount.setUserFeaturesAndRights({
      manageCompanies,
      manageInventory,
      companyManager,
      manageReports,
      companion,
      sve_service,
      eps_service,
    });

    if (!isCloud) {
      return;
    }

    userAccount.isCloud = isCloud;
  }

  public setOverlayContentLoadingState(state: boolean): void {
    const userAccount = window.document.querySelector('user-role') as UserRoleWebComponent;
    if (!userAccount) {
      return;
    }

    userAccount.setLoading(state);
  }

  private onMenuItemOverlayOpen(): void {
    this.setAccountData();
  }
}
